import CircularProgress from '@mui/material/CircularProgress'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { BirthdayOrderForm } from '@components/birthdayOrderForm'
import { BirthdayPackageSelection } from '@components/birthdayPackageSelection'
import { BirthdayProgram } from '@components/birthdayProgram'
import { BirthdayReservationGrid } from '@components/birthdayReservationGrid'
import { BirthdaySummary } from '@components/birthdaySummary'
import { Popover } from '@components/popover'
import {
  BIRTHDAY_PARTY_STARTS,
  BirthdayQuoteRequest,
  BirthdayOrderCustomer,
  BirthdayOrderResponse,
  BirthdaySelectionMode,
  BirthdaySession,
  BirthdayShareSelection,
  birthdaySessionId,
} from '@facades/birthday'
import { useBirthdayAvailability } from '@hooks/useBirthdayAvailability'
import { useBirthdayCatalog } from '@hooks/useBirthdayCatalog'
import { useCreateBirthdayOrder } from '@hooks/useBirthdayOrder'
import { useBirthdayQuote } from '@hooks/useBirthdayQuote'
import { useBirthdayShare, useSaveBirthdayShare } from '@hooks/useBirthdayShare'

import {
  BirthdayReservationAside,
  BirthdayReservationContainer,
  BirthdayReservationLayout,
  BirthdayReservationLoader,
  BirthdayReservationMain,
  BirthdayReservationTitle,
  BirthdayValidationPanel,
} from './birthdayReservation.styled'

function futureDate() {
  const date = new Date()
  date.setDate(date.getDate() + 14)
  return date.toISOString().slice(0, 10)
}

function timeInMinutes(time: string) {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

function shareIdFromUrl() {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('share')
}

export const BirthdayReservation = () => {
  const [shareId, setShareId] = useState(shareIdFromUrl)
  const [date, setDate] = useState(futureDate)
  const [selectionMode, setSelectionMode] =
    useState<BirthdaySelectionMode>('parallel')
  const [duration, setDuration] = useState<150 | 210>(150)
  const [children, setChildren] = useState(10)
  const [parents, setParents] = useState(2)
  const [age, setAge] = useState(10)
  const [selectedSessions, setSelectedSessions] = useState<BirthdaySession[]>(
    []
  )
  const [assignments, setAssignments] = useState<Record<string, number>>({})
  const [supervision, setSupervision] = useState<
    Record<string, 'parent' | 'coach' | 'none'>
  >({})
  const [packageQuantities, setPackageQuantities] = useState<
    Record<string, number>
  >({ pizza: 2 })
  const [pizzaFlavors, setPizzaFlavors] = useState<Record<string, number>>({
    ham: 6,
  })
  const [extraQuantities, setExtraQuantities] = useState<
    Record<string, number>
  >({})
  const [cake, setCake] = useState<BirthdayQuoteRequest['cake']>({
    mode: 'none',
    items: {},
  })
  const [decor, setDecor] = useState<BirthdayQuoteRequest['decor']>('none')
  const [candles, setCandles] = useState(0)
  const [restoredShareId, setRestoredShareId] = useState<string | null>(null)
  const [restoredSessionIds, setRestoredSessionIds] = useState<string[]>([])
  const [savedShareUrl, setSavedShareUrl] = useState<string>()
  const [savedFingerprint, setSavedFingerprint] = useState<string>()
  const [shareError, setShareError] = useState<string>()
  const [copied, setCopied] = useState(false)
  const [orderOpen, setOrderOpen] = useState(false)
  const [orderResult, setOrderResult] = useState<BirthdayOrderResponse>()
  const [orderError, setOrderError] = useState<string>()
  const [orderedFingerprint, setOrderedFingerprint] = useState<string>()
  const [showOrderValidation, setShowOrderValidation] = useState(false)
  const orderValidationRef = useRef<HTMLDivElement>(null)

  const catalog = useBirthdayCatalog()
  const sharedSelection = useBirthdayShare(shareId)
  const availability = useBirthdayAvailability(
    date,
    age,
    !shareId || restoredShareId === shareId || sharedSelection.isError
  )
  const saveShare = useSaveBirthdayShare()
  const createOrder = useCreateBirthdayOrder()
  const rooms = catalog.data?.rooms ?? []

  useEffect(() => {
    const selection = sharedSelection.data?.selection
    if (!shareId || !selection || restoredShareId === shareId) return
    setDate(selection.date)
    setSelectionMode(selection.selectionMode)
    setDuration(selection.duration === 210 ? 210 : 150)
    setChildren(selection.children)
    setParents(selection.parents ?? 0)
    setAge(selection.age)
    setSelectedSessions(selection.sessions)
    setRestoredSessionIds(selection.sessions.map((session) => session.id))
    setAssignments(selection.assignments)
    setSupervision(selection.supervision)
    setPackageQuantities(selection.packages)
    setPizzaFlavors(selection.pizzaFlavors ?? {})
    setExtraQuantities(selection.extras)
    setCake(selection.cake ?? { mode: 'none', items: {} })
    setDecor(selection.decor ?? 'none')
    setCandles(selection.candles ?? 0)
    setRestoredShareId(shareId)
    setShareError(undefined)
  }, [restoredShareId, shareId, sharedSelection.data])

  useEffect(() => {
    setAssignments((current) =>
      selectedSessions.reduce<Record<string, number>>((next, session) => {
        if (current[session.id]) next[session.id] = current[session.id]
        return next
      }, {})
    )
  }, [selectedSessions])

  useEffect(() => {
    if (age >= 13) return
    setSupervision((current) =>
      selectedSessions.reduce(
        (next, session) => ({
          ...next,
          [session.id]: current[session.id] === 'coach' ? 'coach' : 'parent',
        }),
        current
      )
    )
  }, [age, selectedSessions])

  useEffect(() => {
    if (availability.isLoading) return
    setSelectedSessions((current) => {
      const availableSessions = current.filter((session) => {
        const result = availability.data[session.start]
        if (!result) return true
        const available = result.rooms.some(
          (room) => room.roomId === session.roomId && room.available
        )
        return available || restoredSessionIds.includes(session.id)
      })
      return availableSessions.length === current.length
        ? current
        : availableSessions
    })
  }, [availability.data, availability.isLoading, restoredSessionIds])

  const sortedStarts = [
    ...new Set(selectedSessions.map(({ start }) => start)),
  ].sort(
    (left, right) =>
      BIRTHDAY_PARTY_STARTS.indexOf(
        left as (typeof BIRTHDAY_PARTY_STARTS)[number]
      ) -
      BIRTHDAY_PARTY_STARTS.indexOf(
        right as (typeof BIRTHDAY_PARTY_STARTS)[number]
      )
  )
  const start = sortedStarts[0] ?? null
  const calculatedDuration =
    selectionMode === 'staggered' && sortedStarts.length > 1
      ? 150 +
        timeInMinutes(sortedStarts[sortedStarts.length - 1]) -
        timeInMinutes(sortedStarts[0])
      : duration
  const selectedRooms = useMemo(
    () => [...new Set(selectedSessions.map((session) => session.roomId))],
    [selectedSessions]
  )

  const assignedChildren = selectedSessions.reduce(
    (sum, session) => sum + (assignments[session.id] ?? 0),
    0
  )
  const allocationValid =
    selectedSessions.length > 0 && assignedChildren === children
  const hasReservedSelection = selectedSessions.some((session) => {
    if (!restoredSessionIds.includes(session.id)) return false
    const result = availability.data[session.start]
    return Boolean(
      result &&
        !result.rooms.some(
          (room) => room.roomId === session.roomId && room.available
        )
    )
  })

  const shareSelection = useMemo<BirthdayShareSelection>(
    () => ({
      date,
      selectionMode,
      duration: calculatedDuration,
      children,
      parents,
      age,
      sessions: selectedSessions,
      assignments,
      supervision,
      packages: packageQuantities,
      pizzaFlavors,
      extras: extraQuantities,
      cake,
      decor,
      candles,
    }),
    [
      age,
      assignments,
      calculatedDuration,
      children,
      parents,
      date,
      extraQuantities,
      packageQuantities,
      pizzaFlavors,
      selectedSessions,
      selectionMode,
      supervision,
      cake,
      decor,
      candles,
    ]
  )
  const selectionFingerprint = JSON.stringify(shareSelection)
  const activeShareUrl =
    selectionFingerprint === savedFingerprint ? savedShareUrl : undefined

  useEffect(() => {
    if (!restoredShareId || savedFingerprint) return
    setSavedFingerprint(selectionFingerprint)
    setSavedShareUrl(window.location.href)
  }, [restoredShareId, savedFingerprint, selectionFingerprint])

  useEffect(() => {
    if (sharedSelection.isError) {
      setShareError(
        sharedSelection.error?.response?.status === 404
          ? 'Sdílený odkaz neexistuje nebo již vypršel.'
          : 'Sdílený výběr se nepodařilo načíst.'
      )
    }
  }, [sharedSelection.error, sharedSelection.isError])

  const request = useMemo<BirthdayQuoteRequest>(
    () => ({
      date,
      start: start ?? '',
      selectionMode,
      duration: calculatedDuration,
      children,
      parents,
      age,
      selectedRooms,
      sessions: selectedSessions,
      assignments,
      supervision,
      packages: packageQuantities,
      pizzaFlavors,
      extras: extraQuantities,
      cake,
      decor,
      candles,
    }),
    [
      age,
      assignments,
      calculatedDuration,
      children,
      parents,
      date,
      extraQuantities,
      packageQuantities,
      pizzaFlavors,
      selectedRooms,
      selectedSessions,
      selectionMode,
      start,
      supervision,
      cake,
      decor,
      candles,
    ]
  )
  const quote = useBirthdayQuote(
    request,
    selectedSessions.length > 0 && allocationValid
  )
  const estimatedOnsite = useMemo(() => {
    const quantities = {
      ...packageQuantities,
      ...extraQuantities,
      ...(cake.mode === 'ours' ? cake.items : {}),
    }
    const itemsTotal = [
      ...(catalog.data?.packages ?? []),
      ...(catalog.data?.extras ?? []),
      ...(catalog.data?.cakes ?? []),
    ].reduce(
      (total, item) => total + item.price * (quantities[item.id] ?? 0),
      0
    )
    return itemsTotal + candles * (catalog.data?.services.candles.price ?? 0)
  }, [
    cake,
    candles,
    catalog.data?.cakes,
    catalog.data?.extras,
    catalog.data?.packages,
    catalog.data?.services.candles.price,
    extraQuantities,
    packageQuantities,
  ])
  const orderIssues = useMemo(() => {
    const issues: string[] = []
    if (!selectedSessions.length) {
      issues.push('Vyberte alespoň jednu hru a čas oslavy.')
    }
    if (selectedSessions.length && assignedChildren !== children) {
      issues.push(
        `Rozdělte všech ${children} dětí mezi vybrané hry. Aktuálně je přiřazeno ${assignedChildren}.`
      )
    }
    if (hasReservedSelection) {
      issues.push('Některý vybraný termín již není dostupný. Vyberte jiný.')
    }
    if (
      Object.values(packageQuantities).reduce(
        (total, quantity) => total + quantity,
        0
      ) < 1
    ) {
      issues.push('Vyberte alespoň jeden základní balíček občerstvení.')
    }
    const validationMessages: Record<string, string> = {
      room_age: 'Vybraná hra není vhodná pro zadaný věk.',
      room_capacity: 'Počet dětí překračuje kapacitu některé vybrané hry.',
      supervision: 'Doplňte dohled dospělé osoby nebo animátora.',
      pizza: 'Počet vybraných pizz neodpovídá zvoleným balíčkům.',
      cake_mode: 'Vyberte způsob zajištění dortu.',
      cake_selection: 'Vyberte alespoň jeden dort.',
      decor: 'Vyberte platnou variantu dekorace.',
      quantity: 'Zkontrolujte množství vybraných položek.',
      duration: 'Zkontrolujte délku oslavy.',
    }
    quote.data?.validation.forEach((issue) => {
      const message = validationMessages[issue.code]
      if (message) issues.push(message)
    })
    if (quote.isError) {
      issues.push('Aktuální cenu a dostupnost se nepodařilo ověřit.')
    }
    return [...new Set(issues)]
  }, [
    assignedChildren,
    children,
    hasReservedSelection,
    packageQuantities,
    quote.data?.validation,
    quote.isError,
    selectedSessions.length,
  ])

  useEffect(() => {
    if (showOrderValidation && !orderIssues.length) {
      setShowOrderValidation(false)
    }
  }, [orderIssues.length, showOrderValidation])
  const isRestoringSharedSelection = Boolean(
    shareId &&
      !sharedSelection.isError &&
      (sharedSelection.isLoading || restoredShareId !== shareId)
  )
  const isLoadingRestoredSelection = Boolean(
    shareId &&
      restoredShareId === shareId &&
      (availability.isLoading || (allocationValid && quote.isLoading))
  )

  const toggleSlot = (roomId: string, partyStart: string) => {
    const id = birthdaySessionId(roomId, partyStart)
    const session = { id, roomId, start: partyStart }

    setSelectedSessions((current) => {
      if (current.some((item) => item.id === id)) {
        return current.filter((item) => item.id !== id)
      }
      if (
        selectionMode === 'parallel' &&
        current.some((item) => item.start !== partyStart)
      ) {
        return [session]
      }
      return [...current, session]
    })
    setSupervision((current) => ({
      ...current,
      [id]: current[id] ?? (age < 13 ? 'parent' : 'none'),
    }))
  }

  const changeDate = (nextDate: string) => {
    setDate(nextDate)
    setSelectedSessions([])
    setAssignments({})
    setSupervision({})
    setPackageQuantities({})
    setPizzaFlavors({})
    setExtraQuantities({})
    setCake({ mode: 'none', items: {} })
    setDecor('none')
    setCandles(0)
    setRestoredSessionIds([])
  }

  const saveCurrentSelection = async () => {
    setShareError(undefined)
    setCopied(false)
    try {
      const saved = await saveShare.mutateAsync(shareSelection)
      const url = new URL(window.location.href)
      url.searchParams.set('share', saved.id)
      window.history.replaceState({}, '', url.toString())
      setSavedShareUrl(url.toString())
      setSavedFingerprint(selectionFingerprint)
    } catch (error) {
      setShareError('Výběr se nepodařilo uložit. Zkuste to znovu.')
    }
  }

  const copyShareLink = async () => {
    if (!activeShareUrl) return
    if (!navigator.clipboard) {
      setShareError('Odkaz zkopírujte ručně z adresního řádku.')
      return
    }
    try {
      await navigator.clipboard.writeText(activeShareUrl)
      setCopied(true)
      setShareError(undefined)
    } catch (error) {
      setShareError('Odkaz zkopírujte ručně z adresního řádku.')
    }
  }

  const submitOrder = async (customer: BirthdayOrderCustomer) => {
    setOrderError(undefined)
    try {
      const result = await createOrder.mutateAsync({ ...request, customer })
      setOrderResult(result)
      setSelectedSessions([])
      setAssignments({})
      setSupervision({})
      setPackageQuantities({})
      setPizzaFlavors({})
      setExtraQuantities({})
      setCake({ mode: 'none', items: {} })
      setDecor('none')
      setCandles(0)
      setRestoredSessionIds([])
      setSavedShareUrl(undefined)
      setSavedFingerprint(undefined)
      setShareError(undefined)
      setCopied(false)
      setShareId(null)
      setRestoredShareId(null)
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.delete('share')
      window.history.replaceState({}, '', currentUrl.toString())
      setOrderedFingerprint(undefined)
      if (!result.confirmationEmailSent) {
        setOrderError(
          'Objednávka byla uložena, ale potvrzovací e-mail se nepodařilo odeslat. Kontaktujte prosím Questerland.'
        )
      }
      try {
        await availability.refetch()
      } catch (refreshError) {
        setOrderError((current) =>
          [current, 'Dostupnost se nepodařilo obnovit. Obnovte prosím stránku.']
            .filter(Boolean)
            .join(' ')
        )
      }
    } catch (error) {
      const status =
        typeof error === 'object' &&
        error &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response &&
        'status' in error.response
          ? error.response.status
          : undefined
      setOrderError(
        status === 409
          ? 'Některý termín byl právě obsazen. Zavřete formulář a vyberte prosím jiný.'
          : status === 422
          ? 'Objednávka obsahuje neplatné údaje. Zkontrolujte výběr a formulář.'
          : 'Objednávku se nepodařilo uložit. Zkuste to prosím znovu.'
      )
    }
  }

  let shareMessage: string | undefined
  if (sharedSelection.isLoading) {
    shareMessage = 'Načítáme sdílený výběr…'
  } else if (restoredShareId) {
    shareMessage = 'Sdílený výběr byl načten a ověřen.'
  } else if (activeShareUrl) {
    shareMessage = 'Odkaz je platný 30 dní.'
  }

  if (
    catalog.isLoading ||
    isRestoringSharedSelection ||
    isLoadingRestoredSelection
  ) {
    return (
      <BirthdayReservationContainer withLoader>
        <BirthdayReservationLoader role="status" aria-live="polite">
          <CircularProgress color="secondary" />
          <span>
            {shareId
              ? 'Načítáme a ověřujeme váš uložený výběr…'
              : 'Načítáme nabídku oslav…'}
          </span>
        </BirthdayReservationLoader>
      </BirthdayReservationContainer>
    )
  }

  return (
    <BirthdayReservationContainer>
      <BirthdayReservationTitle
        $backgroundImage={window.birthdayHeroContent?.imageUrl}
      >
        <h1>
          {window.birthdayHeroContent?.title ?? 'Dětské narozeninové oslavy'}
        </h1>
        <span>
          {window.birthdayHeroContent?.description ??
            'Vyberte termín, hry a občerstvení pro celou skupinu.'}
        </span>
      </BirthdayReservationTitle>
      <BirthdayReservationLayout>
        <BirthdayReservationMain>
          {showOrderValidation && orderIssues.length > 0 && (
            <BirthdayValidationPanel
              ref={orderValidationRef}
              role="alert"
              aria-live="assertive"
            >
              <strong>Před objednáním dokončete následující:</strong>
              <ul>
                {orderIssues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </BirthdayValidationPanel>
          )}
          <BirthdayReservationGrid
            rooms={rooms}
            availability={availability.data}
            isLoading={availability.isLoading}
            isError={availability.isError}
            date={date}
            selectionMode={selectionMode}
            age={age}
            children={children}
            parents={parents}
            selectedSessions={selectedSessions}
            preservedSessionIds={restoredSessionIds}
            hasReservedSelection={hasReservedSelection}
            assignments={assignments}
            supervision={supervision}
            assignedChildren={assignedChildren}
            allocationValid={allocationValid}
            onDateChange={changeDate}
            onAgeChange={(value) => setAge(Math.max(1, Math.min(18, value)))}
            onChildrenChange={(value) =>
              setChildren(Math.max(1, Math.min(30, value)))
            }
            onParentsChange={(value) =>
              setParents(Math.max(0, Math.min(30, value)))
            }
            onSlotToggle={toggleSlot}
            onAssignmentChange={(sessionId, value) => {
              const session = selectedSessions.find(
                (item) => item.id === sessionId
              )
              const room = rooms.find((item) => item.id === session?.roomId)
              setAssignments((current) => ({
                ...current,
                [sessionId]: Math.max(1, Math.min(room?.capacity ?? 1, value)),
              }))
            }}
            onSupervisionChange={(sessionId, value) =>
              setSupervision((current) => ({ ...current, [sessionId]: value }))
            }
          />
          {start && (
            <BirthdayProgram
              start={start}
              duration={calculatedDuration}
              selectionMode={selectionMode}
              sessions={selectedSessions.map((session) => ({
                ...session,
                roomName:
                  rooms.find((room) => room.id === session.roomId)?.name ??
                  session.roomId,
              }))}
              onDurationChange={setDuration}
            />
          )}
          <BirthdayPackageSelection
            packages={catalog.data?.packages ?? []}
            extras={catalog.data?.extras ?? []}
            cakes={catalog.data?.cakes ?? []}
            decorations={catalog.data?.decor ?? []}
            pizzaFlavorOptions={catalog.data?.pizzaFlavors ?? []}
            ownCakePrice={catalog.data?.services.ownCake.price ?? 0}
            candlePrice={catalog.data?.services.candles.price ?? 0}
            drinksMenuUrl={catalog.data?.drinks.menuUrl}
            children={children}
            packageQuantities={packageQuantities}
            pizzaFlavors={pizzaFlavors}
            extraQuantities={extraQuantities}
            cake={cake}
            decor={decor}
            candles={candles}
            onPackageChange={(itemId, quantity) =>
              setPackageQuantities((current) => {
                if (itemId === 'pizza') {
                  const expected = quantity * 3
                  setPizzaFlavors((flavors) => {
                    const nonHam = Object.entries(flavors).reduce(
                      (total, [id, count]) =>
                        id === 'ham' ? total : total + count,
                      0
                    )
                    return {
                      ...flavors,
                      ham: Math.max(0, expected - nonHam),
                    }
                  })
                }
                return { ...current, [itemId]: quantity }
              })
            }
            onExtraChange={(itemId, quantity) =>
              setExtraQuantities((current) => ({
                ...current,
                [itemId]: quantity,
              }))
            }
            onPizzaFlavorChange={(itemId, quantity) =>
              setPizzaFlavors((current) => ({
                ...current,
                [itemId]: quantity,
              }))
            }
            onCakeModeChange={(mode) =>
              setCake((current) => ({ ...current, mode }))
            }
            onCakeItemChange={(itemId, quantity) =>
              setCake((current) => ({
                ...current,
                items: { ...current.items, [itemId]: quantity },
              }))
            }
            onDecorChange={setDecor}
            onCandlesChange={setCandles}
          />
        </BirthdayReservationMain>
        <BirthdayReservationAside aria-label="Souhrn objednávky">
          <BirthdaySummary
            quote={quote.data}
            estimatedOnsite={estimatedOnsite}
            order={{
              canOrder: orderIssues.length === 0,
              isOrdered: orderedFingerprint === selectionFingerprint,
              onOpen: () => {
                setOrderError(undefined)
                if (orderIssues.length) {
                  setShowOrderValidation(true)
                  window.requestAnimationFrame(
                    () =>
                      orderValidationRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      })
                  )
                  return
                }
                if (orderedFingerprint !== selectionFingerprint) {
                  setOrderResult(undefined)
                }
                setOrderOpen(true)
              },
            }}
            share={{
              canShare: allocationValid,
              isSaving: saveShare.isLoading,
              shareUrl: activeShareUrl,
              copied,
              message: shareMessage,
              error: shareError,
              onSave: saveCurrentSelection,
              onCopy: copyShareLink,
            }}
          />
        </BirthdayReservationAside>
      </BirthdayReservationLayout>
      <Popover
        open={orderOpen}
        title="Dokončení objednávky"
        description="Zkontrolujeme dostupnost a aktuální cenu ještě jednou při odeslání."
        onClose={() => setOrderOpen(false)}
      >
        <BirthdayOrderForm
          isSubmitting={createOrder.isLoading}
          submitError={orderError}
          result={orderResult}
          onSubmit={submitOrder}
        />
      </Popover>
    </BirthdayReservationContainer>
  )
}
